import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCurrency } from "../lib/currency";
import { useTRPC } from "../lib/trpc";

/**
 * Whether the box is sold as a product in the environment's linked Shopify store. Only a rule box
 * (built from the merchant's rules in the Shopify app) can be: for one, the switch turns it on,
 * with a refusal arriving as a toast naming the reason, and off sets the Shopify product to draft.
 * The editor shows this card only for a rule box or a box already on the channel.
 */
export function BoxShopifyChannelCard({ boxId, readOnly }: { boxId: string; readOnly: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const currency = useCurrency();
  const channels = useQuery(trpc.shopify.boxChannels.queryOptions());
  const channel = channels.data?.find((c) => c.boxId === boxId) ?? null;
  const setChannel = useMutation(
    trpc.boxes.setChannel.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.boxChannels.queryKey() });
        toast.success(data.enabled ? "Box is on sale in Shopify." : "Box taken off Shopify.");
      },
    }),
  );

  const status = channel?.productGid
    ? `Product ${channel.productGid.replace("gid://shopify/Product/", "#")} at ${formatAmount(channel.syncedPriceCents ?? 0, currency)}`
    : "Not a Shopify product yet.";

  // One row: the switch, what it does, and where the box stands on Shopify.
  return (
    <Card size="sm">
      <CardContent className="flex-row items-center gap-3">
        <Switch
          id={`sell-on-shopify-${boxId}`}
          checked={channel?.enabled ?? false}
          disabled={readOnly || setChannel.isPending || channels.isLoading}
          onCheckedChange={(enabled) => setChannel.mutate({ boxId, channel: "shopify", enabled })}
        />
        <Label htmlFor={`sell-on-shopify-${boxId}`} className="shrink-0">
          Sell on Shopify
        </Label>
        {channel?.published && <Badge>on sale</Badge>}
        <span className="min-w-0 truncate text-sm text-muted-foreground" title={status}>
          {status}
        </span>
      </CardContent>
    </Card>
  );
}
